export class ConnectionGrantClient {
  constructor(endpoint = null, csrfToken = null) {
    this.endpoint = null;
    this.authorizationEndpoint = null;
    this.releaseEndpoint = null;
    this.csrfToken = csrfToken;
    if (endpoint) this.setEndpoint(endpoint);
  }

  setEndpoint(endpoint) {
    const normalized = String(endpoint || "").trim().replace(/\/+$/, "");
    const suffix = "/connection-grant";
    if (!normalized.startsWith("/") ||
        normalized.startsWith("//") ||
        normalized.includes("\\") ||
        !normalized.endsWith(suffix)) {
      throw new TypeError(
        "The live market-data grant endpoint must be an application-relative connection-grant URL."
      );
    }
    const baseEndpoint = normalized.slice(0, -suffix.length);
    this.endpoint = `${baseEndpoint}${suffix}`;
    this.authorizationEndpoint = `${baseEndpoint}/authorization-status`;
    this.releaseEndpoint = `${baseEndpoint}/connection-release`;
  }

  async create(signal) {
    const grant = await requestJson(
      this.requiredEndpoint(this.endpoint),
      this.csrfToken,
      signal
    );
    validateGrant(grant);
    return Object.freeze({
      provider: String(grant.provider),
      streamUri: String(grant.streamUri),
      authenticationScheme: String(grant.authenticationScheme),
      sid: String(grant.sid),
      expiresAtUtc: String(grant.expiresAtUtc),
      availability: String(grant.availability)
    });
  }

  async check(signal) {
    const status = await requestJson(
      this.requiredEndpoint(this.authorizationEndpoint),
      this.csrfToken,
      signal
    );
    if (status?.authorized !== true || !Number.isFinite(Date.parse(status.recheckAfterUtc))) {
      throw new TypeError("The live market-data authorization response is invalid.");
    }
    return Object.freeze({
      authorized: true,
      recheckAfterUtc: String(status.recheckAfterUtc)
    });
  }

  async revoke(sid) {
    if (!sid) return;
    const response = await fetch(this.requiredEndpoint(this.releaseEndpoint), {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      keepalive: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(this.csrfToken ? { "X-CSRF-TOKEN": this.csrfToken } : {})
      },
      body: JSON.stringify({ sid: String(sid) })
    });
    if (!response.ok) {
      const error = new Error("The live market-data session could not be ended cleanly.");
      error.name = "ConnectionReleaseError";
      error.status = response.status;
      throw error;
    }
  }

  requiredEndpoint(endpoint) {
    if (!endpoint) {
      throw new TypeError("Live market data requires a product-qualified grant endpoint.");
    }
    return endpoint;
  }
}

async function requestJson(endpoint, csrfToken, signal) {
  const response = await fetch(endpoint, {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      ...(csrfToken ? { "X-CSRF-TOKEN": csrfToken } : {})
    },
    signal
  });
  if (!response.ok) {
    let reason = "grant_unavailable";
    let message = "Live market-data authorization is unavailable.";
    try {
      const problem = await response.json();
      reason = String(problem.reason || reason);
      message = String(problem.detail || message);
    } catch {
      // Keep the customer-safe fallback.
    }
    const error = new Error(message);
    error.name = "ConnectionGrantError";
    error.status = response.status;
    error.reason = reason;
    throw error;
  }
  return response.json();
}

function validateGrant(grant) {
  if (!grant || typeof grant !== "object" ||
      grant.provider !== "QuoteMedia" && grant.provider !== "MockQuoteMedia" ||
      grant.authenticationScheme !== "Sid" ||
      !grant.sid || !grant.streamUri ||
      !Number.isFinite(Date.parse(grant.expiresAtUtc))) {
    throw new TypeError("The live market-data grant response is invalid.");
  }
  for (const forbidden of ["username", "password", "enterpriseToken", "webmasterId", "applicationSecret"]) {
    if (Object.hasOwn(grant, forbidden)) {
      throw new TypeError("The live market-data grant contains a forbidden durable credential.");
    }
  }
}
